import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Checkbox,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Tooltip as MuiTooltip,
  Tabs, Tab, Paper, Card, CardHeader, CardContent, CardActions, Avatar, Chip, Stack, Grid, Divider,
  List, ListItemButton, Collapse,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Inventory as InventoryIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsIcon from '@mui/icons-material/Settings';
import TableRowsIcon from '@mui/icons-material/TableRows';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import FolderIcon from '@mui/icons-material/Folder';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase';
import {
  doc,
  onSnapshot,
  setDoc,
  getDoc,
  getDocs,
  query,
  collection,
  orderBy,
  where,
  limit,
  addDoc,
  serverTimestamp,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { v4 as uuid } from 'uuid';
import ColumnSelectorDialog from '../components/ColumnSelectorDialog.js';

/* -------------------------------------------------------------------------- */
/*  1)  Generic debounce hook                                                 */
/* -------------------------------------------------------------------------- */
function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  const timeoutRef = useRef();

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timeoutRef.current);
  }, [value, delay]);

  return debounced;
}

function TabPanel({ value, index, children }) {
  return (
    <div role="tabpanel" hidden={value !== index} aria-labelledby={`tab-${index}`}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function FolderTreeItem({
  node,
  childrenByParent,
  activeFolderId,
  setActiveFolderId,
  expanded,
  setExpanded,
  countsByFolder,
  aggregatedCountsByFolder,
  level = 0,
}) {
  const kids = childrenByParent.get(node.id) || [];
  const isOpen = expanded.has(node.id);
  const isSelected = activeFolderId === node.id;
  const c = countsByFolder.get(node.id)?.total || 0;
  const aggregateStats =
    aggregatedCountsByFolder.get(node.id) || { projects: 0, analyses: 0, presentations: 0, total: c };

  const toggleOpen = () => {
    const next = new Set(expanded);
    if (isOpen) next.delete(node.id); else next.add(node.id);
    setExpanded(next);
  };

  const totalPillStyles =
    aggregateStats.total > 0
      ? {
          bgcolor: 'rgba(59, 130, 246, 0.16)',
          color: '#1d4ed8',
        }
      : {
          bgcolor: 'rgba(148, 163, 184, 0.18)',
          color: 'rgba(71, 85, 105, 0.8)',
        };

  return (
    <>
      <MuiTooltip
        title={
          <Stack spacing={0.3}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {node.title || 'Klasör'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(51, 65, 85, 0.9)' }}>
              Toplam: {aggregateStats.total}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(51, 65, 85, 0.75)' }}>
              Veri Setleri: {aggregateStats.projects}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(51, 65, 85, 0.75)' }}>
              Analizler: {aggregateStats.analyses}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(51, 65, 85, 0.75)' }}>
              Sunumlar: {aggregateStats.presentations}
            </Typography>
          </Stack>
        }
        placement="right"
        enterDelay={500}
      >
        <ListItemButton
          dense
          selected={isSelected}
          onClick={() => setActiveFolderId(node.id)}
          sx={{
            pl: 1.5 + level * 2,
            pr: 1.25,
            py: 0.5,
            borderRadius: 3,
            mb: 0.5,
            color: '#1f2937',
            alignItems: 'flex-start',
            transition: 'background-color 0.2s ease, transform 0.2s ease',
            '&.Mui-selected': {
              bgcolor: 'rgba(96, 165, 250, 0.22)',
              color: '#0f172a',
              transform: 'translateX(2px)',
            },
            '&:hover': {
              bgcolor: 'rgba(148, 163, 184, 0.18)',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'inherit' }}>
              {kids.length > 0 && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleOpen();
                  }}
                  sx={{
                    color: 'inherit',
                    p: 0.25,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'rgba(148, 163, 184, 0.18)', color: '#0f172a' },
                  }}
                >
                  {isOpen ? <ExpandMoreIcon fontSize="inherit" /> : <ChevronRightIcon fontSize="inherit" />}
                </IconButton>
              )}
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 2,
                  bgcolor: isSelected ? 'rgba(59, 130, 246, 0.16)' : 'rgba(148, 163, 184, 0.22)',
                  color: isSelected ? '#1d4ed8' : '#475569',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  flexShrink: 0,
                  transition: 'all 0.2s ease',
                }}
              >
                <FolderIcon fontSize="inherit" />
              </Box>
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Typography variant="body2" noWrap sx={{ fontWeight: isSelected ? 700 : 600 }}>
                {node.title || 'Klasör'}
              </Typography>
              <Box
                component="span"
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  borderRadius: 999,
                  px: 0.9,
                  py: 0.2,
                  lineHeight: 1.4,
                  ...totalPillStyles,
                }}
              >
                {aggregateStats.total}
              </Box>
            </Box>
          </Box>
        </ListItemButton>
      </MuiTooltip>
      {kids.length > 0 && (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            {kids.map((child) => (
              <FolderTreeItem
                key={child.id}
                node={child}
                childrenByParent={childrenByParent}
                activeFolderId={activeFolderId}
                setActiveFolderId={setActiveFolderId}
                expanded={expanded}
                setExpanded={setExpanded}
                countsByFolder={countsByFolder}
                aggregatedCountsByFolder={aggregatedCountsByFolder}
                level={level + 1}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
} 

/* -------------------------------------------------------------------------- */
/*  2)  Header‑group parser (JS port of Cloud Function)                       */
/* -------------------------------------------------------------------------- */
const groupCache = new Map();

function determineHeaderGroup(header) {
  if (groupCache.has(header)) return groupCache.get(header);

  if (!header) {
    groupCache.set(header, null);
    return null;
  }

  // Başlık parçala: tek/multi alt‑çizgi veya nokta hepsi ayraç kabul edilsin
  const parts = header.split(/[_.]+/);
  // Ör.: M8_5.21 → ["M8","5","21"] – ikinci parça saf sayıysa multiplechoice kabul et
  if (parts.length >= 2 && /^\d+$/.test(parts[1])) {
    const info = { groupname: parts[0] };
    info.grouptype = 'multiplechoice';
    info.m = parts[1];
    groupCache.set(header, info);
    return info;
  }

  if (parts.length < 2) {
    groupCache.set(header, null);
    return null;
  }
  const info = { groupname: parts[0] };

  if (parts.includes('CODE')) {
    const idx = parts.indexOf('CODE');
    info.grouptype = 'multiplechoice';
    if (parts[idx + 1]) info.m = parts[idx + 1];
    groupCache.set(header, info);
    return info;
  }

  if (parts.length === 2) {
    const suf = parts[1];
    if (/^\d+$/.test(suf)) {
      info.grouptype = 'multiplechoice';
      info.m = suf;
      groupCache.set(header, info);
      return info;
    }
    if (/^[rc]\d+$/.test(suf)) {
      info.grouptype = 'griddata';
      info[suf[0]] = suf.slice(1);
      groupCache.set(header, info);
      return info;
    }
    return null;
  }

  if (parts.length === 3) {
    let r = null,
      c = null;
    parts.slice(1).forEach((p) => {
      if (p.startsWith('r') && /^\d+$/.test(p.slice(1))) r = p.slice(1);
      if (p.startsWith('c') && /^\d+$/.test(p.slice(1))) c = p.slice(1);
    });
    if (r && c) {
      info.grouptype = 'multigriddata';
      info.r = r;
      info.c = c;
      groupCache.set(header, info);
      return info;
    }
  }
  groupCache.set(header, null);
  return null;
}

/* -------------------------------------------------------------------------- */
/*  3)  Helpers for first‑time auto‑grouping                                  */
/* -------------------------------------------------------------------------- */
function hasGrouping(project) {
  return project?.rawData?.some((c) => c.groupname);
}

function autoGroupColumns(rawData = []) {
  return rawData.map((col) => {
    if (col.groupname) return col; // already grouped
    const meta = determineHeaderGroup(col.header) || {};
    const nc = { ...col };
    // Eğer kullanıcı bu kolonu elle "ungroup" yaptıysa, tekrar otomatik
    // gruplama uygulama. Her ihtimale karşı eski grup alanlarını temizle.
    if (nc.manualUngroup) {
      delete nc.groupname;
      delete nc.grouptype;
      delete nc.m;
      delete nc.r;
      delete nc.c;
      if (nc.active === undefined) nc.active = true;
      return nc;
    }

    if (meta.groupname) {
      nc.groupname = meta.groupname;
      nc.grouptype = meta.grouptype || 'multiplechoice';
      if (meta.m) nc.m = meta.m;
      if (meta.r) nc.r = meta.r;
      if (meta.c) nc.c = meta.c;
    }
    if (nc.active === undefined) {
      nc.active = !['griddata', 'multigriddata'].includes(nc.grouptype);
    }
    return nc;
  });
}


/* -------------------------------------------------------------------------- */
/*  5)  Main component – ProjectsPage                                         */
/* -------------------------------------------------------------------------- */
export default function ProjectsPage() {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [apiLoading, setApiLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [organisation, setOrganisation] = useState('');

  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectorProject, setSelectorProject] = useState(null);
  const [renameProjectOpen, setRenameProjectOpen] = useState(false);
  const [renameProjectTitle, setRenameProjectTitle] = useState('');
  const [renameProjectTarget, setRenameProjectTarget] = useState(null);

  const [presentations, setPresentations] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newPresentationTitle, setNewPresentationTitle] = useState('');
  const [newPresentationFolderId, setNewPresentationFolderId] = useState('');
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameTitle, setRenameTitle] = useState('');
  const [renameTarget, setRenameTarget] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [createAnalysisOpen, setCreateAnalysisOpen] = useState(false);
  const [newAnalysisTitle, setNewAnalysisTitle] = useState('');
  const [newAnalysisProjectID, setNewAnalysisProjectID] = useState('');
  const [newAnalysisFolderId, setNewAnalysisFolderId] = useState('');
  const [renameAnalysisOpen, setRenameAnalysisOpen] = useState(false);
  const [renameAnalysisTitle, setRenameAnalysisTitle] = useState('');
  const [renameAnalysisTarget, setRenameAnalysisTarget] = useState(null);
  const [changeAnalysisOpen, setChangeAnalysisOpen] = useState(false);
  const [changeAnalysisTarget, setChangeAnalysisTarget] = useState(null);
  const [changeAnalysisProjectID, setChangeAnalysisProjectID] = useState('');

  const [activeTab, setActiveTab] = useState(0);

  const debouncedSearch = useDebounce(searchTerm);

  // Folder state
  const [folders, setFolders] = useState([]);
  const [activeFolderId, setActiveFolderId] = useState(''); // '' => All
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderTitle, setNewFolderTitle] = useState('');
  const [newFolderParentId, setNewFolderParentId] = useState('');
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  const [renameFolderOpen, setRenameFolderOpen] = useState(false);
  const [renameFolderTitle, setRenameFolderTitle] = useState('');

  const [deleteFolderConfirmOpen, setDeleteFolderConfirmOpen] = useState(false);
  const columnSelectorRef = useRef(null);
  const processedAutoResetCodesRef = useRef(new Set());
  const [autoResetSpec, setAutoResetSpec] = useState(null);

  // Generic "move to folder" dialog
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null); // {type: 'project'|'analysis'|'presentation', row}
  const [moveFolderId, setMoveFolderId] = useState('');

  // Normalize various timestamp/date shapes -> JS Date
  const toDateSafe = (v) => {
    if (!v) return null;

    // Firestore Timestamp (has toDate)
    if (typeof v?.toDate === 'function') return v.toDate();

    // Firestore Timestamp-like object {seconds, nanoseconds}
    if (typeof v?.seconds === 'number') return new Date(v.seconds * 1000);

    // Milliseconds since epoch
    if (typeof v === 'number') return new Date(v);

    // String formats
    if (typeof v === 'string') {
      // Try native parse (ISO etc.)
      let d = new Date(v);
      if (!isNaN(d.getTime())) return d;

      // "YYYY-MM-DD HH:mm:ss" or "YYYY/MM/DD HH:mm:ss"
      let m = v.match(/^(\d{4})[-/.](\d{2})[-/.](\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/);
      if (m) {
        const [, y, mo, da, h, mi, s] = m;
        return new Date(Number(y), Number(mo) - 1, Number(da), Number(h), Number(mi), Number(s));
      }

      // "DD.MM.YYYY" / "DD/MM/YYYY" / "DD-MM-YYYY"
      m = v.match(/^(\d{2})[./-](\d{2})[./-](\d{4})$/);
      if (m) {
        const [, da, mo, y] = m;
        return new Date(Number(y), Number(mo) - 1, Number(da));
      }

      return null;
    }

    return null;
  };
  const showLoading = () => setApiLoading(true);
  const hideLoading = () => setApiLoading(false);

  const projectsRef = useRef([]);
  const [allProjectsMap, setAllProjectsMap] = useState(new Map());

  /* ------------ Firestore listener ---*/
  useEffect(() => {
    if (!currentUser?.uid) return;

    setLoading(true);
    let unsub = () => {};

    (async () => {
      try {
        const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
        const org = userSnap.data()?.organisation;
        setOrganisation(org);

        if (!org) {
          setProjects([]);
          setLoading(false);
          return;
        }

        const projectsColRef = collection(db, 'organisations', org, 'projects');
        const q = query(projectsColRef, orderBy('createDate', 'desc'));

        unsub = onSnapshot(q, (snap) => {
          const newProjects = snap.docs.map((d) => ({ projectID: d.id, id: d.id, ...d.data() }));

          // Backfill missing createDate for currently listed projects
          snap.docs.forEach((d) => {
            const data = d.data();
            if (!data.createDate) {
              updateDoc(doc(db, 'organisations', org, 'projects', d.id), {
                createDate: serverTimestamp(),
              }).catch((e) => console.error('backfill (filtered) project.createDate failed', e));
            }
          });

          // Deep compare before updating state
          if (JSON.stringify(projectsRef.current) !== JSON.stringify(newProjects)) {
            projectsRef.current = newProjects;
            setProjects(newProjects);
          }

          setLoading(false);
        });
      } catch (e) {
        console.error(e);
        setLoading(false);
      }
    })();

    return () => unsub();
  }, [currentUser?.uid]);

  /* ------------ all projects map (for lookups regardless of status) ---*/
  useEffect(() => {
    if (!organisation) return () => {};
    const qAll = query(collection(db, 'organisations', organisation, 'projects'));
    return onSnapshot(qAll, (snap) => {
      const m = new Map();
      snap.docs.forEach((d) => {
        const data = d.data();
        m.set(d.id, { ...data, projectID: d.id });
        // Backfill missing createDate on projects
        if (!data.createDate) {
          updateDoc(doc(db, 'organisations', organisation, 'projects', d.id), {
            createDate: serverTimestamp(),
          }).catch((e) => console.error('backfill project.createDate failed', e));
        }
      });
      setAllProjectsMap(m);
    });
  }, [organisation]);

  /* ------------ search filter --------*/
  const filteredProjects = useMemo(() => {
    if (!debouncedSearch) return projects;
    const n = debouncedSearch.toLowerCase();
    return projects.filter((p) => p.projectCode.toLowerCase().includes(n));
  }, [projects, debouncedSearch]);

  /* ------------ save cols to Firestore --------------*/
  const saveColsToFirestore = useCallback(
    async (projID, newRaw) => {
      if (!organisation) return;

      // Sanitize: undefined bırakma, tipleri netleştir
      const sanitized = (Array.isArray(newRaw) ? newRaw : []).map((c) => {
        const out = {
          header: String(c?.header || ''),
          active: c?.active === false ? false : true,
        };

        const assignTrimmed = (key) => {
          const raw = c?.[key];
          if (raw === undefined || raw === null) return;
          if (typeof raw === 'object') return;
          const str = String(raw).trim();
          if (str) out[key] = str;
        };

        assignTrimmed('groupname');
        assignTrimmed('grouptype');
        assignTrimmed('type');
        assignTrimmed('id');
        assignTrimmed('groupLabel');
        assignTrimmed('varLabel');
        assignTrimmed('otherTextHeader');
        assignTrimmed('otherCodeHeader');

        if (Array.isArray(c?.otherCodeHeaders)) out.otherCodeHeaders = c.otherCodeHeaders.slice();
        if (Array.isArray(c?.options) && c.options.length) out.options = c.options;
        if (c?.groupVisible !== undefined) out.groupVisible = !!c.groupVisible;
        if (c?.manualUngroup) out.manualUngroup = true;
        if (c?.m !== undefined && c.m !== null) out.m = String(c.m);
        if (c?.r !== undefined && c.r !== null) out.r = String(c.r);
        if (c?.c !== undefined && c.c !== null) out.c = String(c.c);
        if (typeof c?.order === 'number' && Number.isFinite(c.order)) out.order = c.order;

        if (c?.valueLabels && typeof c.valueLabels === 'object' && Object.keys(c.valueLabels).length) {
          out.valueLabels = c.valueLabels;
        }

        if (c?.isLikert) {
          out.isLikert = true;
          if (Array.isArray(c.likertScale) && c.likertScale.length) {
            out.likertScale = c.likertScale;
          }
        }
        if (c?.groupLikert) {
          out.groupLikert = true;
          if (Array.isArray(c.groupLikertScale) && c.groupLikertScale.length) {
            out.groupLikertScale = c.groupLikertScale;
          }
        }

        // Meta payloads (sys_*, type=meta) carry complex objects that must persist as-is.
        const isMeta = /^sys_/i.test(out.header) || (typeof out.type === 'string' && out.type === 'meta');
        if (isMeta) {
          Object.entries(c || {}).forEach(([key, value]) => {
            if (value === undefined || value === null) return;
            if (typeof value === 'function') return;
            if (key in out) return;
            out[key] = value;
          });
        }

        // Ensure backend always receives a type hint.
        if (!out.type) {
          if (out.grouptype === 'griddata' || out.grouptype === 'multigriddata') {
            out.type = out.grouptype;
          } else {
            out.type = 'multi-select data';
          }
        }

        return out;
      });

      console.log('[saveColsToFirestore] writing', projID, sanitized);

      await setDoc(
        doc(db, 'organisations', organisation, 'projects', projID),
        { rawData: sanitized },
        { merge: true }
      );

      setProjects((prev) =>
        prev.map((p) => (p.projectID === projID ? { ...p, rawData: sanitized } : p))
      );
      setSnackbar({ open: true, message: t('saved'), severity: 'success' });
    },
    [organisation, t]
  );

  // ------------------- Project Rename/Delete handlers -------------------
  const openRenameProjectDialog = (row) => {
    setRenameProjectTarget(row);
    setRenameProjectTitle(row.projectCode || '');
    setRenameProjectOpen(true);
  };

  const handleRenameProject = async () => {
    if (!renameProjectTarget || !organisation) return;
    try {
      await updateDoc(doc(db, 'organisations', organisation, 'projects', renameProjectTarget.projectID), {
        projectCode: (renameProjectTitle || '').trim() || renameProjectTarget.projectCode,
      });
      setRenameProjectOpen(false);
    } catch (err) {
      console.error('project rename error', err);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  const handleDeleteProject = async (row) => {
    if (!organisation || !row?.projectID) return;
    try {
      // Check if there are analyses linked to this project
      const qAnalyses = query(
        collection(db, 'organisations', organisation, 'analyses'),
        where('projectID', '==', row.projectID),
        limit(1)
      );
      const snap = await getDocs(qAnalyses);
      if (!snap.empty) {
        window.alert('Bu veri setine bağlı analizler var. Lütfen önce bu analizleri silin.');
        return;
      }
      const ok = window.confirm('Bu veri setini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.');
      if (!ok) return;
      await deleteDoc(doc(db, 'organisations', organisation, 'projects', row.projectID));
    } catch (err) {
      console.error('project delete error', err);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  /* ------------ open selector --------*/
  const openHeaderSelector = useCallback(
    async (project) => {
      try {
        // Her açılışta Firestore'dan en güncel dokümanı çek
        const projRef = doc(db, 'organisations', organisation, 'projects', project.projectID);
        const snap = await getDoc(projRef);
        const fresh = snap.exists() ? { ...project, ...snap.data(), projectID: project.projectID } : project;

        // İlk defa açılıyorsa otomatik gruplandır, değilse mevcut durumu koru
        const alreadyGrouped = hasGrouping(fresh);
        const newRaw = alreadyGrouped ? (fresh.rawData || []) : autoGroupColumns(fresh.rawData || []);

        if (!alreadyGrouped) {
          await saveColsToFirestore(fresh.projectID, newRaw);
        }

        // Diyaloğu en güncel veriyle aç
        const targetProject = { ...fresh, rawData: newRaw };
        setSelectorProject(targetProject);
        setSelectorOpen(true);
      } catch (err) {
        console.error('openHeaderSelector error', err);
        setSnackbar({ open: true, severity: 'error', message: err.message || 'Açılış hatası' });
      }
    },
    [saveColsToFirestore, organisation]
  );

  useEffect(() => {
    const stateCode = location?.state?.resetProjectCode;
    const trimmedCode = typeof stateCode === 'string' ? stateCode.trim() : '';
    if (!trimmedCode || processedAutoResetCodesRef.current.has(trimmedCode)) return;
    if (!projects.length || !organisation) return;
    const target = projects.find(
      (p) => (p.projectCode || '').trim() === trimmedCode
    );
    if (!target) return;
    processedAutoResetCodesRef.current.add(trimmedCode);
    (async () => {
      await openHeaderSelector(target);
      setAutoResetSpec({ projectID: target.projectID, projectCode: trimmedCode });
      navigate('.', { replace: true });
    })();
  }, [location?.state?.resetProjectCode, projects, organisation, openHeaderSelector, navigate]);

  useEffect(() => {
    if (!autoResetSpec || !selectorOpen || !selectorProject?.projectID) return;
    if (selectorProject.projectID !== autoResetSpec.projectID) return;
    const api = columnSelectorRef.current;
    if (api && typeof api.reset === 'function') {
      api.reset();
      setAutoResetSpec(null);
    }
  }, [autoResetSpec, selectorOpen, selectorProject]);

  /* ------------ dashboard nav ----------------*/
  const viewGraphsDashboard = useCallback(
    async (proj, opts = {}) => {
      if (!proj || !proj.projectID) return;
      if (!organisation) {
        console.warn('Organisation missing — cannot open graphs dashboard.');
        return;
      }

      const { analysisId, fresh } = opts || {};
      showLoading();
      try {
        let cached = null;
        if (!fresh) {
          try {
            const refs = [];
            if (analysisId) {
              refs.push(
                collection(db, 'organisations', organisation, 'analyses', analysisId, 'graphDatasets')
              );
            }
            refs.push(
              collection(db, 'organisations', organisation, 'projects', proj.projectID, 'graphDatasets')
            );

            const snaps = await Promise.all(refs.map((r) => getDocs(r)));
            const all = snaps.flatMap((s) =>
              s.docs.map((d) => ({ id: d.id, ...d.data() }))
            );

            const toMs = (v) => {
              const d =
                (v && typeof v.toDate === 'function' && v.toDate()) ||
                (v && typeof v.seconds === 'number' && new Date(v.seconds * 1000)) ||
                (typeof v === 'number' && new Date(v)) ||
                (typeof v === 'string' && new Date(v)) ||
                null;
              return d instanceof Date && !isNaN(d.getTime()) ? d.getTime() : 0;
            };

            if (all.length) {
              all.sort((a, b) => toMs(b.createdAt || b.createDate) - toMs(a.createdAt || a.createDate));
              cached = all[0];
            }
          } catch (cacheErr) {
            console.error('Cache lookup failed – proceeding without cached dataset', cacheErr);
          }
        }

        const columnTypes = Array.isArray(cached?.columnTypes)
          ? cached.columnTypes
          : Array.isArray(cached?.meta?.columnTypes)
            ? cached.meta.columnTypes
            : Array.isArray(proj.rawData)
              ? proj.rawData
              : [];

        const nextState = {
          projectID: proj.projectID,
          analysisID: analysisId || null,
          columnTypes,
        };

        if (cached) {
          nextState.data = cached;
        }

        navigate('/GraphsDashboards', { state: nextState });
      } catch (err) {
        console.error(err);
        setSnackbar({ open: true, message: err.message || t('Error'), severity: 'error' });
      } finally {
        hideLoading();
      }
    },
    [organisation, navigate, setSnackbar, showLoading, hideLoading, t]
  );

  // Open analysis dashboard for a given analysis row (finds project and opens dashboard)
  const openAnalysis = useCallback(
    async (analysisRow, opts = {}) => {
      const proj = projects.find((p) => p.projectID === analysisRow.projectID);
      if (!proj) {
        setSnackbar({ open: true, severity: 'error', message: 'Seçilen veri seti bulunamadı.' });
        return;
      }
      await viewGraphsDashboard(proj, { analysisId: analysisRow.id, fresh: opts?.fresh });
    },
    [projects, viewGraphsDashboard]
  );

  const projectLabelById = useCallback((pid) => {
    const info = allProjectsMap.get(pid) || projects.find((pr) => pr.projectID === pid);
    return (
      info?.projectCode || info?.title || info?.name || info?.projectName || pid || ''
    );
  }, [allProjectsMap, projects]);

  /* --------------------------------------------------------------
   *  Presentation create / rename / delete logic
   * --------------------------------------------------------------*/
  const openCreateDialog = () => {
    setNewPresentationTitle('');
    setNewPresentationFolderId(activeFolderId || '');
    setCreateDialogOpen(true);
  };

  const handleCreatePresentation = async () => {
    if (!organisation) return;
    try {
      const title = newPresentationTitle.trim() || `Sunum – ${new Date().toLocaleDateString()}`;
      const presRef = await addDoc(collection(db, 'organisations', organisation, 'presentations'), {
        title,
        createDate: serverTimestamp(),
        createdBy: currentUser.uid,
        folderID: newPresentationFolderId || null,
        status: 'Active',
        slides: [{ id: uuid(), items: [] }],
      });
      setCreateDialogOpen(false);
      navigate(`/presentation/${presRef.id}`);
    } catch (err) {
      console.error('presentation create error', err);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  const openRenameDialog = (row) => {
    setRenameTarget(row);
    setRenameTitle(row.title || '');
    setRenameDialogOpen(true);
  };

  const handleRenamePresentation = async () => {
    if (!renameTarget || !organisation) return;
    try {
      await updateDoc(doc(db, 'organisations', organisation, 'presentations', renameTarget.id), {
        title: renameTitle.trim() || renameTarget.title,
      });
      setRenameDialogOpen(false);
    } catch (err) {
      console.error('rename error', err);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  const handleDeletePresentation = async (row) => {
    if (!organisation || !row?.id) return;
    const confirmDelete = window.confirm('Sunumu silmek istediğinize emin misiniz?');
    if (!confirmDelete) return;
    try {
      await deleteDoc(doc(db, 'organisations', organisation, 'presentations', row.id));
    } catch (err) {
      console.error('delete error', err);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  /* --------------------------------------------------------------
   *  Analysis create / open / rename / delete logic
   * --------------------------------------------------------------*/
  const openCreateAnalysisDialog = () => {
    setNewAnalysisTitle('');
    setNewAnalysisProjectID('');
    setNewAnalysisFolderId(activeFolderId || '');
    setCreateAnalysisOpen(true);
  };

  const handleCreateAnalysis = async () => {
    if (!organisation) return;
    if (!newAnalysisProjectID) {
      setSnackbar({ open: true, severity: 'error', message: 'Lütfen bir veri seti seçin.' });
      return;
    }
    try {
      const title =
        newAnalysisTitle.trim() || `Analiz – ${new Date().toLocaleDateString()}`;
      const docRef = await addDoc(
        collection(db, 'organisations', organisation, 'analyses'),
        {
          title,
          projectID: newAnalysisProjectID,
          createDate: serverTimestamp(),
          createdBy: currentUser.uid,
          folderID: newAnalysisFolderId || null,
          status: 'Active',
        }
      );
      setCreateAnalysisOpen(false);
      // Açılışta doğrudan ilgili veri setiyle analizi aç
      openAnalysis({ id: docRef.id, title, projectID: newAnalysisProjectID }, { fresh: true });
    } catch (err) {
      console.error('analysis create error', err);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };
  /* --------------------------------------------------------------
   *  Listen folders for organisation
   * --------------------------------------------------------------*/
  useEffect(() => {
    if (!organisation) return () => {};
    const q = query(
      collection(db, 'organisations', organisation, 'folders'),
      orderBy('createDate', 'desc')
    );
    return onSnapshot(q, (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Backfill missing createDate on folders
      snap.docs.forEach((d) => {
        const data = d.data();
        if (!data.createDate) {
          updateDoc(doc(db, 'organisations', organisation, 'folders', d.id), {
            createDate: serverTimestamp(),
          }).catch((e) => console.error('backfill folder.createDate failed', e));
        }
      });
      setFolders(arr);
    });
  }, [organisation]);

  // Create a new folder
  const handleCreateFolder = async () => {
    if (!organisation) return;
    try {
      const title = newFolderTitle.trim() || `Klasör – ${new Date().toLocaleDateString()}`;
      await addDoc(collection(db, 'organisations', organisation, 'folders'), {
        title,
        parentID: newFolderParentId || (activeFolderId || '') || '',
        createDate: serverTimestamp(),
        createdBy: currentUser.uid,
        status: 'Active',
      });
      setCreateFolderOpen(false);
      setNewFolderTitle('');
      setNewFolderParentId('');
    } catch (err) {
      console.error('folder create error', err);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  const openRenameFolder = () => {
    if (!activeFolderId) return;
    const f = folderById.get(activeFolderId);
    setRenameFolderTitle(f?.title || '');
    setRenameFolderOpen(true);
  };

  const handleRenameFolder = async () => {
    if (!organisation || !activeFolderId) return;
    try {
      const newTitle = (renameFolderTitle || '').trim();
      if (!newTitle) return setRenameFolderOpen(false);
      await updateDoc(doc(db, 'organisations', organisation, 'folders', activeFolderId), { title: newTitle });
      setRenameFolderOpen(false);
    } catch (err) {
      console.error('folder rename error', err);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  const openDeleteFolderConfirm = () => {
    if (!activeFolderId) return;
    setDeleteFolderConfirmOpen(true);
  };

  const handleDeleteFolder = async () => {
    if (!organisation || !activeFolderId) return;
    try {
      const hasChildren = (childrenByParent.get(activeFolderId) || []).length > 0;
      const hasItems = (countsByFolder.get(activeFolderId)?.total || 0) > 0;
      if (hasChildren || hasItems) {
        setSnackbar({ open: true, severity: 'warning', message: 'Klasör boş değil. İçinde klasör veya öğe varken silinemez.' });
        setDeleteFolderConfirmOpen(false);
        return;
      }
      await deleteDoc(doc(db, 'organisations', organisation, 'folders', activeFolderId));
      setActiveFolderId('');
      setDeleteFolderConfirmOpen(false);
    } catch (err) {
      console.error('folder delete error', err);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  // Open move dialog for a given row and type
  const openMoveToFolder = (type, row) => {
    setMoveTarget({ type, row });
    setMoveFolderId(row.folderID || activeFolderId || '');
    setMoveDialogOpen(true);
  };

  // Persist move to folder for a project/analysis/presentation
  const handleMoveToFolder = async () => {
    if (!organisation || !moveTarget) return;
    try {
      const { type, row } = moveTarget;
      const colName =
        type === 'project' ? 'projects' : type === 'analysis' ? 'analyses' : 'presentations';
      await updateDoc(doc(db, 'organisations', organisation, colName, row.id || row.projectID), {
        folderID: moveFolderId || null,
      });
      setMoveDialogOpen(false);
      setMoveTarget(null);
      setMoveFolderId('');
    } catch (err) {
      console.error('move to folder error', err);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  // counts per folder (direct items only)
  const countsByFolder = useMemo(() => {
    const m = new Map();
    const bump = (fid, key) => {
      if (!fid) return;
      if (!m.has(fid)) m.set(fid, { projects: 0, analyses: 0, presentations: 0, total: 0 });
      const o = m.get(fid);
      o[key] += 1;
      o.total += 1;
    };
    projects.forEach(p => p.folderID && bump(p.folderID, 'projects'));
    analyses.forEach(a => a.folderID && bump(a.folderID, 'analyses'));
    presentations.forEach(pr => pr.folderID && bump(pr.folderID, 'presentations'));
    return m;
  }, [projects, analyses, presentations]);

  // id → folder
  const folderById = useMemo(() => {
    const m = new Map();
    folders.forEach(f => m.set(f.id, f));
    return m;
  }, [folders]);

  // parentID → çocuk listesi (kök için '' kullanıyoruz)
  const childrenByParent = useMemo(() => {
    const m = new Map();
    folders.forEach(f => {
      const pid = f.parentID || '';
      if (!m.has(pid)) m.set(pid, []);
      m.get(pid).push(f);
    });
    // kardeşleri isme göre sırala
    m.forEach(arr => arr.sort((a,b) => (a.title||'').localeCompare(b.title||'')));
    return m;
  }, [folders]);

  const aggregatedCountsByFolder = useMemo(() => {
    const totals = new Map();
    const ensureTotals = (id) => {
      if (totals.has(id)) return totals.get(id);
      const base = countsByFolder.get(id) || { projects: 0, analyses: 0, presentations: 0, total: 0 };
      const aggregate = { ...base };
      const children = childrenByParent.get(id) || [];
      children.forEach((child) => {
        const childTotals = ensureTotals(child.id);
        aggregate.projects += childTotals.projects;
        aggregate.analyses += childTotals.analyses;
        aggregate.presentations += childTotals.presentations;
        aggregate.total += childTotals.total;
      });
      totals.set(id, aggregate);
      return aggregate;
    };
    folders.forEach((folder) => ensureTotals(folder.id));
    return totals;
  }, [childrenByParent, countsByFolder, folders]);

  // Klasör filtreleme: sadece seçili klasörün DOĞRUDAN içindekileri göster
  const isRootSelected = activeFolderId === '';
  const isSomeFolderSelected = !!activeFolderId && activeFolderId !== '';

  const filteredProjectsByFolder = useMemo(() => {
    const base = filteredProjects;
    if (isRootSelected) return base.filter((p) => !p.folderID);
    if (isSomeFolderSelected) return base.filter((p) => p.folderID === activeFolderId);
    return base;
  }, [filteredProjects, isRootSelected, isSomeFolderSelected, activeFolderId]);

  const analysesByFolder = useMemo(() => {
    if (isRootSelected) return analyses.filter((a) => !a.folderID);
    if (isSomeFolderSelected) return analyses.filter((a) => a.folderID === activeFolderId);
    return analyses;
  }, [analyses, isRootSelected, isSomeFolderSelected, activeFolderId]);

  const presentationsByFolder = useMemo(() => {
    if (isRootSelected) return presentations.filter((pr) => !pr.folderID);
    if (isSomeFolderSelected) return presentations.filter((pr) => pr.folderID === activeFolderId);
    return presentations;
  }, [presentations, isRootSelected, isSomeFolderSelected, activeFolderId]);

  const unassignedCounts = useMemo(() => {
    const projectsCount = projects.filter((p) => !p.folderID).length;
    const analysesCount = analyses.filter((a) => !a.folderID).length;
    const presentationsCount = presentations.filter((p) => !p.folderID).length;
    return {
      projects: projectsCount,
      analyses: analysesCount,
      presentations: presentationsCount,
      total: projectsCount + analysesCount + presentationsCount,
    };
  }, [projects, analyses, presentations]);

  const workspaceTotals = useMemo(() => {
    const projectsCount = projects.length;
    const analysesCount = analyses.length;
    const presentationsCount = presentations.length;
    return {
      projects: projectsCount,
      analyses: analysesCount,
      presentations: presentationsCount,
      total: projectsCount + analysesCount + presentationsCount,
    };
  }, [projects, analyses, presentations]);

  const activeFolderRollup = useMemo(() => {
    if (!activeFolderId) return unassignedCounts;
    return (
      aggregatedCountsByFolder.get(activeFolderId) || { projects: 0, analyses: 0, presentations: 0, total: 0 }
    );
  }, [activeFolderId, aggregatedCountsByFolder, unassignedCounts]);

  const openRenameAnalysisDialog = (row) => {
    setRenameAnalysisTarget(row);
    setRenameAnalysisTitle(row.title || '');
    setRenameAnalysisOpen(true);
  };

  const handleRenameAnalysis = async () => {
    if (!renameAnalysisTarget || !organisation) return;
    try {
      await updateDoc(
        doc(db, 'organisations', organisation, 'analyses', renameAnalysisTarget.id),
        { title: renameAnalysisTitle.trim() || renameAnalysisTarget.title }
      );
      setRenameAnalysisOpen(false);
    } catch (err) {
      console.error('analysis rename error', err);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  const openChangeAnalysisDialog = (row) => {
    setChangeAnalysisTarget(row);
    setChangeAnalysisProjectID(row.projectID || '');
    setChangeAnalysisOpen(true);
  };

  const handleChangeAnalysisProject = async () => {
    if (!changeAnalysisTarget || !organisation) return;
    if (!changeAnalysisProjectID) {
      setSnackbar({ open: true, severity: 'error', message: 'Lütfen bir veri seti seçin.' });
      return;
    }
    try {
      await updateDoc(
        doc(db, 'organisations', organisation, 'analyses', changeAnalysisTarget.id),
        { projectID: changeAnalysisProjectID }
      );
      setChangeAnalysisOpen(false);
    } catch (err) {
      console.error('analysis change dataset error', err);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  const handleDeleteAnalysis = async (row) => {
    if (!organisation || !row?.id) return;
    const confirmDelete = window.confirm('Analizi silmek istediğinize emin misiniz?');
    if (!confirmDelete) return;
    try {
      await deleteDoc(doc(db, 'organisations', organisation, 'analyses', row.id));
    } catch (err) {
      console.error('analysis delete error', err);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  const handleDuplicateAnalysis = async (row) => {
    if (!organisation || !row?.id) return;
    try {
      // Read existing analysis doc
      const srcRef = doc(db, 'organisations', organisation, 'analyses', row.id);
      const srcSnap = await getDoc(srcRef);
      if (!srcSnap.exists()) {
        setSnackbar({ open: true, severity: 'error', message: 'Analiz bulunamadı.' });
        return;
      }
      const src = srcSnap.data() || {};

      // Build new analysis payload (copy selected fields)
      const newTitle = `${(src.title || row.title || 'Analiz')} (Kopya)`;
      const payload = {
        title: newTitle,
        projectID: src.projectID || row.projectID,
        createDate: serverTimestamp(),
        createdBy: currentUser.uid,
        status: src.status || 'Active',
      };
      // Optional fields: savedFilters / savedPivots / savedCrosstabs
      if (Array.isArray(src.savedFilters)) payload.savedFilters = JSON.parse(JSON.stringify(src.savedFilters));
      if (Array.isArray(src.savedPivots)) payload.savedPivots = JSON.parse(JSON.stringify(src.savedPivots));
      if (Array.isArray(src.savedCrosstabs)) payload.savedCrosstabs = JSON.parse(JSON.stringify(src.savedCrosstabs));

      const newRef = await addDoc(collection(db, 'organisations', organisation, 'analyses'), payload);

      setSnackbar({ open: true, severity: 'success', message: 'Analiz kopyalandı.' });

      // Optionally open the new analysis with fresh cache
      openAnalysis({ id: newRef.id, title: newTitle, projectID: payload.projectID }, { fresh: true });
    } catch (err) {
      console.error('analysis duplicate error', err);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  /* --------------------------------------------------------------
   *  Listen presentations for organisation
   * --------------------------------------------------------------*/
  useEffect(() => {
    if (!organisation) return () => {};
    const q = query(
      collection(db, 'organisations', organisation, 'presentations'),
      orderBy('createDate', 'desc')
    );
    return onSnapshot(q, (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Backfill missing createDate on presentations
      snap.docs.forEach((d) => {
        const data = d.data();
        if (!data.createDate) {
          updateDoc(doc(db, 'organisations', organisation, 'presentations', d.id), {
            createDate: serverTimestamp(),
          }).catch((e) => console.error('backfill presentation.createDate failed', e));
        }
      });
      setPresentations(arr);
    });
  }, [organisation]);

  /* --------------------------------------------------------------
   *  Listen analyses for organisation
   * --------------------------------------------------------------*/
  useEffect(() => {
    if (!organisation) return () => {};
    const q = query(
      collection(db, 'organisations', organisation, 'analyses'),
      orderBy('createDate', 'desc')
    );
    return onSnapshot(q, (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Backfill missing createDate on analyses
      snap.docs.forEach((d) => {
        const data = d.data();
        if (!data.createDate) {
          updateDoc(doc(db, 'organisations', organisation, 'analyses', d.id), {
            createDate: serverTimestamp(),
          }).catch((e) => console.error('backfill analysis.createDate failed', e));
        }
      });
      setAnalyses(arr);
    });
  }, [organisation]);

  /* --------------------------------------------------------------
   *  Render
   * --------------------------------------------------------------*/
  const funCardSx = (type) => {
    const gradientMap = {
      project: 'linear-gradient(135deg, #bfdbfe 0%, #dbeafe 100%)',
      analysis: 'linear-gradient(135deg, #bbf7d0 0%, #dcfce7 100%)',
      presentation: 'linear-gradient(135deg, #fbcfe8 0%, #fef3c7 100%)',
    };

    return {
      borderRadius: 4,
      overflow: 'hidden',
      background: 'linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)',
      border: '1px solid rgba(148, 163, 184, 0.18)',
      boxShadow: '0 16px 32px rgba(148, 163, 184, 0.2)',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
      position: 'relative',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 20px 36px rgba(148, 163, 184, 0.28)',
        borderColor: 'rgba(148, 163, 184, 0.32)',
      },
      '& .cardHeader': {
        background: gradientMap[type] || gradientMap.project,
        color: '#1e293b',
        py: 1.5,
        '& .MuiCardHeader-title': {
          fontWeight: 700,
        },
        '& .MuiCardHeader-subheader': {
          color: 'rgba(51, 65, 85, 0.7)',
        },
      },
    };
  };
  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {t('loading')}
        </Typography>
      </Container>
    );
  }

  const currentFolderTitle = activeFolderId ? (folderById.get(activeFolderId)?.title || 'Klasör') : 'Klasörsüz Öğeler';
  const projectsInFolder = filteredProjectsByFolder.length;
  const analysesInFolder = analysesByFolder.length;
  const presentationsInFolder = presentationsByFolder.length;
  const itemsInFolder = projectsInFolder + analysesInFolder + presentationsInFolder;

  const statsCards = [
    {
      id: 'datasets',
      label: 'Veri Setleri',
      value: projects.length,
      helper: `${projectsInFolder} seçili klasörde`,
      accent: '#60a5fa',
      icon: InventoryIcon,
    },
    {
      id: 'analyses',
      label: 'Analizler',
      value: analyses.length,
      helper: `${analysesInFolder} seçili klasörde`,
      accent: '#a78bfa',
      icon: AssessmentIcon,
    },
    {
      id: 'presentations',
      label: 'Sunumlar',
      value: presentations.length,
      helper: `${presentationsInFolder} seçili klasörde`,
      accent: '#fbbf24',
      icon: SlideshowIcon,
    },
  ];

  const subtleChipSx = {
    borderColor: 'rgba(148, 163, 184, 0.4)',
    bgcolor: 'rgba(224, 231, 255, 0.6)',
    color: '#1f2937',
    fontWeight: 600,
    '& .MuiChip-icon': { color: '#3b82f6' },
  };

  const surfaceBaseSx = {
    px: { xs: 2, md: 3 },
    py: { xs: 2, md: 2.5 },
  };

  const basePrimaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    px: { xs: 2, md: 2.5 },
    py: 0.95,
    borderRadius: 999,
    boxShadow: '0 12px 24px rgba(148, 163, 184, 0.28)',
    color: '#0f172a',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    '&:hover': {
      boxShadow: '0 16px 28px rgba(148, 163, 184, 0.32)',
    },
  };

  const primaryActionThemes = {
    projects: {
      background: 'linear-gradient(135deg, #bfdbfe 0%, #dbeafe 100%)',
      '&:hover': {
        background: 'linear-gradient(135deg, #93c5fd 0%, #bfdbfe 100%)',
      },
    },
    analyses: {
      background: 'linear-gradient(135deg, #bbf7d0 0%, #dcfce7 100%)',
      '&:hover': {
        background: 'linear-gradient(135deg, #86efac 0%, #bbf7d0 100%)',
      },
    },
    presentations: {
      background: 'linear-gradient(135deg, #fbcfe8 0%, #fde68a 100%)',
      '&:hover': {
        background: 'linear-gradient(135deg, #f9a8d4 0%, #fcd34d 100%)',
      },
    },
  };

  const basePrimaryButtonHoverSx = basePrimaryButtonSx['&:hover'] || {};

  const dialogPaperSx = {
    borderRadius: 3,
    background: 'linear-gradient(150deg, rgba(255,255,255,0.96) 0%, rgba(241,245,249,0.96) 100%)',
    border: '1px solid rgba(148, 163, 184, 0.3)',
    boxShadow: '0 28px 48px rgba(15,23,42,0.18)',
    overflow: 'hidden',
  };

  const dialogBaseProps = {
    maxWidth: 'xs',
    fullWidth: true,
    PaperProps: { sx: dialogPaperSx },
  };

  const dialogTitleSx = {
    fontWeight: 700,
    color: '#0f172a',
    px: { xs: 3, md: 3.5 },
    py: { xs: 2.5, md: 3 },
    background: 'linear-gradient(135deg, rgba(191, 219, 254, 0.75) 0%, rgba(224, 231, 255, 0.75) 100%)',
    borderBottom: '1px solid rgba(148, 163, 184, 0.22)',
  };

  const dialogContentSx = {
    px: { xs: 3, md: 3.5 },
    py: { xs: 2, md: 2.5 },
    backgroundColor: 'rgba(248, 250, 252, 0.92)',
    '&.MuiDialogContent-dividers': {
      borderColor: 'rgba(148, 163, 184, 0.24)',
    },
  };

  const dialogActionsSx = {
    px: { xs: 3, md: 3.5 },
    py: { xs: 2, md: 2.5 },
    backgroundColor: 'rgba(241, 245, 249, 0.9)',
    borderTop: '1px solid rgba(148, 163, 184, 0.18)',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 1.5,
  };

  const dialogFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2.5,
      backgroundColor: '#fff',
      '&:hover fieldset': { borderColor: 'rgba(59, 130, 246, 0.5)' },
      '&.Mui-focused fieldset': {
        borderColor: '#3b82f6',
        boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.15)',
      },
    },
  };

  const dialogPrimaryButtonSx = {
    ...basePrimaryButtonSx,
    px: { xs: 2.75, md: 3 },
    py: 0.9,
    background: 'linear-gradient(135deg, #bfdbfe 0%, #e0e7ff 100%)',
    '&:hover': {
      ...basePrimaryButtonHoverSx,
      background: 'linear-gradient(135deg, #93c5fd 0%, #bfdbfe 100%)',
    },
  };

  const dialogPrimaryButtonHoverSx = dialogPrimaryButtonSx['&:hover'] || {};

  const dialogSecondaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    color: '#475569',
    px: { xs: 2.25, md: 2.75 },
    borderRadius: 999,
    '&:hover': {
      backgroundColor: 'rgba(148, 163, 184, 0.12)',
    },
  };

  const buildPrimaryActions = (options = {}) => {
    const {
      buttonSx = {},
      fullWidth = false,
      stackProps = {},
    } = options;

    const {
      sx: stackSx = {},
      spacing = 1,
      direction = 'column',
      alignItems = 'stretch',
      ...restStackProps
    } = stackProps;

    const baseProps = {
      variant: 'contained',
      startIcon: <AddIcon />,
      fullWidth,
    };

    const actions = [
      {
        key: 'dataset',
        label: t('NewDataSet'),
        onClick: () => navigate('/new-project'),
        theme: primaryActionThemes.projects,
      },
      {
        key: 'analysis',
        label: 'Yeni Analiz',
        onClick: openCreateAnalysisDialog,
        theme: primaryActionThemes.analyses,
      },
      {
        key: 'presentation',
        label: t('PresentationBuilder'),
        onClick: openCreateDialog,
        theme: primaryActionThemes.presentations,
      },
    ];

    return (
      <Stack
        spacing={spacing}
        direction={direction}
        alignItems={alignItems}
        sx={{ width: fullWidth ? '100%' : 'auto', ...stackSx }}
        {...restStackProps}
      >
        {actions.map(({ key, label, onClick, theme }) => (
          <Button
            key={key}
            {...baseProps}
            onClick={onClick}
            sx={{ ...basePrimaryButtonSx, ...theme, ...buttonSx }}
          >
            {label}
          </Button>
        ))}
      </Stack>
    );
  };

  const primaryActionsHero = buildPrimaryActions({
    fullWidth: true,
    buttonSx: { width: { xs: '100%', md: 'auto' } },
    stackProps: { alignItems: { xs: 'stretch', md: 'flex-end' } },
  });
  const primaryActionsCompact = buildPrimaryActions({
    fullWidth: true,
    buttonSx: { width: '100%' },
    stackProps: { alignItems: 'stretch' },
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f8fbff 0%, #eef2ff 50%, #fdf2f8 100%)',
        py: { xs: 3, md: 4.5 },
        px: 0,
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 1.5, md: 3 } }}>
        <Stack spacing={2.25}>
          <Box
            sx={{
              borderRadius: 4,
              color: '#0f172a',
              border: '1px solid rgba(148, 163, 184, 0.2)',
              background: 'linear-gradient(135deg, rgba(191, 219, 254, 0.9) 0%, rgba(244, 219, 255, 0.9) 100%)',
              boxShadow: '0 20px 40px rgba(148, 163, 184, 0.25)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <Box sx={{ px: { xs: 2.5, md: 3 }, py: { xs: 2.5, md: 3 } }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
              >
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    Dashboard
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1.5 }}>
                    <Chip icon={<FolderIcon />} label={`Klasör: ${currentFolderTitle}`} sx={subtleChipSx} />
                    <Chip icon={<TableRowsIcon />} label={`${itemsInFolder} içerik`} sx={subtleChipSx} />
                  </Stack>
                </Box>
                {primaryActionsHero && (
                      <Stack
                        spacing={0.75}
                        alignItems={{ xs: 'stretch', md: 'flex-end' }}
                        sx={{ width: { xs: '100%', md: 'auto' } }}
                      >
                        {primaryActionsHero}
                        <Typography
                          variant="caption"
                          sx={{ opacity: 0.8, maxWidth: 240, textAlign: { xs: 'left', md: 'right' } }}
                        >
                          Hızlıca yeni veri seti, analiz veya sunum başlat.
                        </Typography>
                      </Stack>
                  )
                }
              </Stack>
            </Box>
          </Box>

          <Box sx={{ px: { xs: 1.5, md: 2 }, py: { xs: 1, md: 1.5 } }}>
            <Grid container spacing={1}>
              {statsCards.map((card) => {
                const Icon = card.icon;
                return (
                  <Grid item xs={12} sm={4} key={card.id}>
                    <Paper
                      sx={{
                        borderRadius: 4,
                        ...surfaceBaseSx,
                        background: 'linear-gradient(145deg, rgba(255,255,255,0.96) 0%, rgba(241,245,249,0.9) 100%)',
                        border: `1px solid ${card.accent}33`,
                        boxShadow: '0 16px 32px rgba(148, 163, 184, 0.18)',
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          sx={{
                            bgcolor: `${card.accent}22`,
                            color: card.accent,
                            width: 44,
                            height: 44,
                          }}
                        >
                          <Icon />
                        </Avatar>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'rgba(15,23,42,0.6)' }}
                          >
                            {card.label}
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a' }}>
                            {card.value}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(15,23,42,0.6)' }}>
                            {card.helper}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          <Box sx={{ px: { xs: 1.5, md: 2 }, py: { xs: 1, md: 1.5 } }}>
              <Grid container spacing={1} alignItems="stretch">
                <Grid item xs={12} md={4} lg={3}>
                  <Paper
                    sx={{
                      borderRadius: 4,
                      ...surfaceBaseSx,
                      background: 'linear-gradient(160deg, rgba(255,255,255,0.96) 0%, rgba(241,245,249,0.94) 100%)',
                      color: '#0f172a',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      border: '1px solid rgba(148, 163, 184, 0.25)',
                      boxShadow: '0 20px 36px rgba(148, 163, 184, 0.24)',
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: 'rgba(51,65,85,0.7)' }}>
                        Klasörler
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Çalışma Alanı
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(51, 65, 85, 0.7)', display: 'block', mt: 0.5 }}>
                        {workspaceTotals.total} öğe · {workspaceTotals.projects} Veri Seti · {workspaceTotals.analyses} Analiz · {workspaceTotals.presentations} Sunum
                      </Typography>
                    </Box>
                    <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.22)' }} />
                    <Box
                      sx={{
                        borderRadius: 3,
                        border: '1px solid rgba(148, 163, 184, 0.25)',
                        backgroundColor: 'rgba(255, 255, 255, 0.85)',
                        maxHeight: 360,
                        overflow: 'auto',
                        p: 1.25,
                      }}
                    >
                      <List dense disablePadding>
                        <MuiTooltip
                          title={
                            <Stack spacing={0.3}>
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                Klasörsüz Öğeler
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(51, 65, 85, 0.9)' }}>
                                Toplam: {unassignedCounts.total}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(51, 65, 85, 0.75)' }}>
                                Veri Setleri: {unassignedCounts.projects}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(51, 65, 85, 0.75)' }}>
                                Analizler: {unassignedCounts.analyses}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(51, 65, 85, 0.75)' }}>
                                Sunumlar: {unassignedCounts.presentations}
                              </Typography>
                            </Stack>
                          }
                          placement="right"
                          enterDelay={500}
                        >
                          <ListItemButton
                            dense
                            selected={!activeFolderId}
                            onClick={() => setActiveFolderId('')}
                            sx={{
                              borderRadius: 3,
                              mb: 0.5,
                              color: '#1f2937',
                              px: 1.5,
                              py: 0.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              transition: 'background-color 0.2s ease, transform 0.2s ease',
                              '&.Mui-selected': {
                                bgcolor: 'rgba(96, 165, 250, 0.24)',
                                color: '#0f172a',
                                transform: 'translateX(2px)',
                              },
                              '&:hover': {
                                bgcolor: 'rgba(148, 163, 184, 0.18)',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: 30,
                                height: 30,
                                borderRadius: 2,
                                bgcolor: !activeFolderId ? 'rgba(59, 130, 246, 0.16)' : 'rgba(148, 163, 184, 0.22)',
                                color: !activeFolderId ? '#1d4ed8' : '#475569',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <FolderIcon fontSize="small" />
                            </Box>
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: !activeFolderId ? 700 : 600 }}>
                                Klasörsüz Öğeler
                              </Typography>
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 12,
                                  fontWeight: 700,
                                  borderRadius: 999,
                                  px: 0.9,
                                  py: 0.2,
                                  lineHeight: 1.4,
                                  bgcolor: unassignedCounts.total > 0 ? 'rgba(59, 130, 246, 0.16)' : 'rgba(148, 163, 184, 0.18)',
                                  color: unassignedCounts.total > 0 ? '#1d4ed8' : 'rgba(71, 85, 105, 0.8)',
                                }}
                              >
                                {unassignedCounts.total}
                              </Box>
                            </Box>
                          </ListItemButton>
                        </MuiTooltip>
                        {(childrenByParent.get('') || []).map((root) => (
                          <FolderTreeItem
                            key={root.id}
                            node={root}
                            childrenByParent={childrenByParent}
                            activeFolderId={activeFolderId}
                            setActiveFolderId={setActiveFolderId}
                            expanded={expandedFolders}
                            setExpanded={setExpandedFolders}
                            countsByFolder={countsByFolder}
                            aggregatedCountsByFolder={aggregatedCountsByFolder}
                          />
                        ))}
                      </List>
                    </Box>
                    <Box
                      sx={{
                        borderRadius: 3,
                        border: '1px dashed rgba(148, 163, 184, 0.45)',
                        backgroundColor: 'rgba(226, 232, 240, 0.35)',
                        p: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      <Typography variant="overline" sx={{ letterSpacing: 1, color: 'rgba(51, 65, 85, 0.65)' }}>
                        Seçili Klasör Özeti
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                        {currentFolderTitle}
                      </Typography>
                      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                        <Chip
                          size="small"
                          icon={<TableRowsIcon sx={{ fontSize: 16 }} />}
                          label={`${activeFolderRollup.total} öğe`}
                          sx={{
                            bgcolor: 'rgba(15, 23, 42, 0.08)',
                            color: '#0f172a',
                            borderRadius: 2,
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: '#0f172a' },
                            opacity: activeFolderRollup.total === 0 ? 0.6 : 1,
                          }}
                        />
                        <Chip
                          size="small"
                          icon={<InventoryIcon sx={{ fontSize: 16 }} />}
                          label={`${activeFolderRollup.projects} Veri Seti`}
                          sx={{
                            bgcolor: 'rgba(59, 130, 246, 0.14)',
                            color: '#1d4ed8',
                            borderRadius: 2,
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: '#1d4ed8' },
                            opacity: activeFolderRollup.projects === 0 ? 0.6 : 1,
                          }}
                        />
                        <Chip
                          size="small"
                          icon={<AssessmentIcon sx={{ fontSize: 16 }} />}
                          label={`${activeFolderRollup.analyses} Analiz`}
                          sx={{
                            bgcolor: 'rgba(249, 115, 22, 0.14)',
                            color: '#b45309',
                            borderRadius: 2,
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: '#b45309' },
                            opacity: activeFolderRollup.analyses === 0 ? 0.6 : 1,
                          }}
                        />
                        <Chip
                          size="small"
                          icon={<SlideshowIcon sx={{ fontSize: 16 }} />}
                          label={`${activeFolderRollup.presentations} Sunum`}
                          sx={{
                            bgcolor: 'rgba(16, 185, 129, 0.16)',
                            color: '#047857',
                            borderRadius: 2,
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: '#047857' },
                            opacity: activeFolderRollup.presentations === 0 ? 0.6 : 1,
                          }}
                        />
                      </Stack>
                    </Box>
                    <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.2)' }} />
                    <Stack direction="column" spacing={1}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<CreateNewFolderIcon />}
                        onClick={() => {
                          setNewFolderParentId(activeFolderId || '');
                          setCreateFolderOpen(true);
                        }}
                        sx={{
                          textTransform: 'none',
                          bgcolor: 'rgba(96, 165, 250, 0.9)',
                          '&:hover': { bgcolor: 'rgba(96, 165, 250, 1)' },
                        }}
                      >
                        Yeni Klasör
                      </Button>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          disabled={!activeFolderId}
                          onClick={openRenameFolder}
                          sx={{
                            textTransform: 'none',
                            color: '#0f172a',
                            borderColor: 'rgba(148, 163, 184, 0.45)',
                            '&:hover': { borderColor: 'rgba(148, 163, 184, 0.75)' },
                          }}
                        >
                          Yeniden Adlandır
                        </Button>
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          disabled={!activeFolderId}
                          onClick={openDeleteFolderConfirm}
                          sx={{
                            textTransform: 'none',
                            borderColor: 'rgba(248, 113, 113, 0.5)',
                            color: 'rgba(248, 113, 113, 0.9)',
                            '&:hover': {
                              borderColor: 'rgba(248, 113, 113, 0.9)',
                            },
                          }}
                        >
                          Sil
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={8} lg={9}>
                  <Stack spacing={2.25}>
                    <Paper
                      sx={{
                    borderRadius: 4,
                    ...surfaceBaseSx,
                    display: 'flex',
                    flexDirection: { xs: 'column', xl: 'row' },
                    gap: 1.5,
                    alignItems: { xs: 'stretch', xl: 'center' },
                    justifyContent: 'space-between',
                        boxShadow: '0 12px 24px rgba(148, 163, 184, 0.18)',
                        border: '1px solid rgba(148, 163, 184, 0.16)',
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Veri seti, analiz veya sunum ara..."
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Paper>

                    <Paper
                      sx={{
                        borderRadius: 4,
                        overflow: 'hidden',
                        border: '1px solid rgba(148, 163, 184, 0.16)',
                        boxShadow: '0 18px 36px rgba(148, 163, 184, 0.2)',
                        px: { xs: 0, md: 0 },
                        mr: { xs: 3, md: 4 },
                        py: 0,
                      }}
                    >
                      <Box
                        sx={{
                          px: { xs: 2.5, md: 3 },
                          py: { xs: 1.25, md: 1.75 },
                          display: 'flex',
                          flexDirection: { xs: 'column', md: 'row' },
                          alignItems: { xs: 'flex-start', md: 'center' },
                          justifyContent: 'space-between',
                          gap: 1.5,
                          backgroundColor: '#ffffff',
                        }}
                      >
                        <Tabs
                          value={activeTab}
                          onChange={(_, val) => setActiveTab(val)}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{
                            minHeight: 0,
                            '& .MuiTab-root': {
                              textTransform: 'none',
                              fontWeight: 600,
                              minHeight: 44,
                              borderRadius: 999,
                              mr: 1,
                              px: 1.75,
                              color: '#475569',
                              '&.Mui-selected': {
                                color: '#0f172a',
                                backgroundColor: 'rgba(96, 165, 250, 0.16)',
                              },
                            },
                            '& .MuiTabs-indicator': {
                              height: 0,
                            },
                          }}
                        >
                          <Tab
                            id="tab-0"
                            icon={<InventoryIcon fontSize="small" />}
                            iconPosition="start"
                            label={`Veri Setleri (${filteredProjectsByFolder.length || 0})`}
                            disableRipple
                          />
                          <Tab
                            id="tab-1"
                            icon={<AssessmentIcon fontSize="small" />}
                            iconPosition="start"
                            label={`Analizler (${analysesByFolder.length || 0})`}
                            disableRipple
                          />
                          <Tab
                            id="tab-2"
                            icon={<SlideshowIcon fontSize="small" />}
                            iconPosition="start"
                            label={`Sunumlar (${presentationsByFolder.length || 0})`}
                            disableRipple
                          />
                        </Tabs>
                        {primaryActionsCompact && (
                          <Box sx={{ width: '100%', display: { xs: 'block', md: 'none' } }}>
                            {primaryActionsCompact}
                          </Box>
                        )}
                      </Box>
                      <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.2)' }} />
                      <Box sx={{ px: { xs: 2.5, md: 3 }, py: { xs: 2, md: 2.5 }, backgroundColor: '#f8fafc' }}>
                        <TabPanel value={activeTab} index={0}>
                          <Grid container spacing={2}>
                            {filteredProjectsByFolder.map((p) => (
                              <Grid item key={p.projectID} xs={12} sm={6} md={4}>
                                <Card sx={funCardSx('project')}>
                                  <CardHeader
                                    className="cardHeader"
                                    avatar={
                                      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.7)', color: '#0f172a' }}>
                                        <InventoryIcon />
                                      </Avatar>
                                    }
                                    titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
                                    subheaderTypographyProps={{ variant: 'caption' }}
                                    title={p.projectCode}
                                    subheader={(p.createDate && toDateSafe(p.createDate)?.toLocaleDateString()) || '—'}
                                  />
                                  <CardContent>
                                    <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                                      {Array.isArray(p.rawData) && (
                                        <Chip size="small" label={`${p.rawData.length} kolon`} />
                                      )}
                                      {p.status && <Chip size="small" label={p.status} />}
                                    </Stack>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                      Veri seti başlıklarını düzenleyin, yeni analizler oluşturun veya klasörlere taşıyın.
                                    </Typography>
                                  </CardContent>
                                  <CardActions sx={{ justifyContent: 'space-between' }}>
                                    <Button size="small" startIcon={<SettingsIcon />} onClick={() => openHeaderSelector(p)}>
                                      Ayarlar
                                    </Button>
                                    <Stack direction="row" spacing={1}>
                                      <IconButton size="small" onClick={() => openMoveToFolder('project', p)} title="Klasöre Taşı">
                                        <DriveFileMoveIcon fontSize="inherit" />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => openRenameProjectDialog(p)} title="Yeniden Adlandır">
                                        <EditIcon fontSize="inherit" />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => handleDeleteProject(p)} title="Sil">
                                        <DeleteIcon fontSize="inherit" />
                                      </IconButton>
                                    </Stack>
                                  </CardActions>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        </TabPanel>
                        <TabPanel value={activeTab} index={1}>
                          <Grid container spacing={2}>
                            {analysesByFolder.map((a) => (
                              <Grid item key={a.id} xs={12} sm={6} md={4}>
                                <Card sx={funCardSx('analysis')}>
                                  <CardHeader
                                    className="cardHeader"
                                    avatar={
                                      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.7)', color: '#0f172a' }}>
                                        <AssessmentIcon />
                                      </Avatar>
                                    }
                                    titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
                                    subheaderTypographyProps={{ variant: 'caption' }}
                                    title={a.title || 'Analiz'}
                                    subheader={projectLabelById(a.projectID)}
                                  />
                                  <CardContent>
                                    <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                                      <Chip size="small" icon={<InventoryIcon />} label={projectLabelById(a.projectID)} />
                                      {a.status && <Chip size="small" label={a.status} />}
                                    </Stack>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                      Grafikleri açın, farklı veri setleriyle karşılaştırın veya sunumlara ekleyin.
                                    </Typography>
                                  </CardContent>
                                  <CardActions sx={{ justifyContent: 'space-between' }}>
                                    <Button
                                      size="small"
                                      startIcon={<OpenInNewIcon />}
                                      onClick={() => openAnalysis({ id: a.id, title: a.title, projectID: a.projectID })}
                                    >
                                      Aç
                                    </Button>
                                    <Stack direction="row" spacing={1}>
                                      <IconButton size="small" onClick={() => openMoveToFolder('analysis', a)} title="Klasöre Taşı">
                                        <DriveFileMoveIcon fontSize="inherit" />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => handleDuplicateAnalysis(a)} title="Kopyala">
                                        <ContentCopyIcon fontSize="inherit" />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => openRenameAnalysisDialog(a)} title="Yeniden Adlandır">
                                        <EditIcon fontSize="inherit" />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => handleDeleteAnalysis(a)} title="Sil">
                                        <DeleteIcon fontSize="inherit" />
                                      </IconButton>
                                    </Stack>
                                  </CardActions>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        </TabPanel>
                        <TabPanel value={activeTab} index={2}>
                          <Grid container spacing={2}>
                            {presentationsByFolder.map((pr) => (
                              <Grid item key={pr.id} xs={12} sm={6} md={4}>
                                <Card sx={funCardSx('presentation')}>
                                  <CardHeader
                                    className="cardHeader"
                                    avatar={
                                      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.7)', color: '#0f172a' }}>
                                        <SlideshowIcon />
                                      </Avatar>
                                    }
                                    titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
                                    subheaderTypographyProps={{ variant: 'caption' }}
                                    title={pr.title || 'Sunum'}
                                    subheader={(pr.createDate && toDateSafe(pr.createDate)?.toLocaleDateString()) || ''}
                                  />
                                  <CardContent>
                                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 1.5 }}>
                                      Sunum taslaklarını aç, düzenle ve ekibinle anında paylaş.
                                    </Typography>
                                    <Stack direction="row" spacing={1}>
                                      <Chip size="small" icon={<SlideshowIcon />} label="Slides" />
                                      {pr.status && <Chip size="small" label={pr.status} />}
                                    </Stack>
                                  </CardContent>
                                  <CardActions sx={{ justifyContent: 'space-between' }}>
                                    <Button size="small" startIcon={<OpenInNewIcon />} onClick={() => navigate(`/presentation/${pr.id}`)}>
                                      Aç
                                    </Button>
                                    <Stack direction="row" spacing={1}>
                                      <IconButton size="small" onClick={() => openMoveToFolder('presentation', pr)} title="Klasöre Taşı">
                                        <DriveFileMoveIcon fontSize="inherit" />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => openRenameDialog(pr)} title="Yeniden Adlandır">
                                        <EditIcon fontSize="inherit" />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => handleDeletePresentation(pr)} title="Sil">
                                        <DeleteIcon fontSize="inherit" />
                                      </IconButton>
                                    </Stack>
                                  </CardActions>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        </TabPanel>
                      </Box>
                    </Paper>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
        </Stack>
      </Container>

      <Dialog
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        {...dialogBaseProps}
      >
        <DialogTitle sx={dialogTitleSx}>Yeni Klasör Oluştur</DialogTitle>
        <DialogContent dividers sx={dialogContentSx}>
          <TextField
            autoFocus
            fullWidth
            label="Klasör Başlığı"
            value={newFolderTitle}
            onChange={(e) => setNewFolderTitle(e.target.value)}
            sx={dialogFieldSx}
          />
          <TextField
            fullWidth
            select
            label="Üst Klasör"
            value={newFolderParentId}
            onChange={(e) => setNewFolderParentId(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ ...dialogFieldSx, mt: 2 }}
          >
            <option value="">(En üst seviye)</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>{f.title || 'Klasör'}</option>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setCreateFolderOpen(false)} sx={dialogSecondaryButtonSx}>
            {t('cancel')}
          </Button>
          <Button variant="contained" onClick={handleCreateFolder} sx={dialogPrimaryButtonSx}>
            Oluştur
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={renameFolderOpen}
        onClose={() => setRenameFolderOpen(false)}
        {...dialogBaseProps}
      >
        <DialogTitle sx={dialogTitleSx}>Klasör Adını Değiştir</DialogTitle>
        <DialogContent dividers sx={dialogContentSx}>
          <TextField
            autoFocus
            fullWidth
            label="Klasör Başlığı"
            value={renameFolderTitle}
            onChange={(e) => setRenameFolderTitle(e.target.value)}
            sx={dialogFieldSx}
          />
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setRenameFolderOpen(false)} sx={dialogSecondaryButtonSx}>
            {t('cancel')}
          </Button>
          <Button variant="contained" onClick={handleRenameFolder} sx={dialogPrimaryButtonSx}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteFolderConfirmOpen}
        onClose={() => setDeleteFolderConfirmOpen(false)}
        {...dialogBaseProps}
      >
        <DialogTitle sx={dialogTitleSx}>Klasörü Sil</DialogTitle>
        <DialogContent dividers sx={dialogContentSx}>
          <Typography variant="body2" sx={{ color: 'rgba(15, 23, 42, 0.8)' }}>
            Bu klasörü silmek istediğinize emin misiniz? Klasörün boş olması gerekir (içinde veri seti, analiz, sunum veya alt klasör olmamalı).
          </Typography>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setDeleteFolderConfirmOpen(false)} sx={dialogSecondaryButtonSx}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteFolder}
            sx={{
              ...dialogPrimaryButtonSx,
              background: 'linear-gradient(135deg, #fecaca 0%, #fca5a5 100%)',
              border: '1px solid rgba(248, 113, 113, 0.35)',
              color: '#7f1d1d',
              '&:hover': {
                ...dialogPrimaryButtonHoverSx,
                background: 'linear-gradient(135deg, #fca5a5 0%, #f87171 100%)',
                color: '#7f1d1d',
              },
            }}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
        {...dialogBaseProps}
      >
        <DialogTitle sx={dialogTitleSx}>Klasöre Taşı</DialogTitle>
        <DialogContent dividers sx={dialogContentSx}>
          <TextField
            fullWidth
            select
            label="Klasör"
            value={moveFolderId}
            onChange={(e) => setMoveFolderId(e.target.value)}
            SelectProps={{ native: true }}
            sx={dialogFieldSx}
          >
            <option value="">Klasörsüz Öğeler</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>{f.title || 'Klasör'}</option>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setMoveDialogOpen(false)} sx={dialogSecondaryButtonSx}>
            {t('cancel')}
          </Button>
          <Button variant="contained" onClick={handleMoveToFolder} sx={dialogPrimaryButtonSx}>
            Taşı
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      {apiLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <ColumnSelectorDialog
        ref={columnSelectorRef}
        key={`${selectorProject?.projectID || 'no-proj'}-${selectorOpen ? 'open' : 'closed'}`}
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        project={selectorProject}
        onSave={(projID, newRaw) => {
          return saveColsToFirestore(projID, newRaw);
        }}
      />

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        {...dialogBaseProps}
      >
        <DialogTitle sx={dialogTitleSx}>Yeni Sunum Oluştur</DialogTitle>
        <DialogContent dividers sx={dialogContentSx}>
          <TextField
            autoFocus
            fullWidth
            label="Sunum Başlığı"
            value={newPresentationTitle}
            onChange={(e) => setNewPresentationTitle(e.target.value)}
            sx={dialogFieldSx}
          />
          <TextField
            fullWidth
            select
            label="Klasör"
            value={newPresentationFolderId}
            onChange={(e) => setNewPresentationFolderId(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ ...dialogFieldSx, mt: 2 }}
          >
            <option value="">Klasörsüz Öğeler</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>{f.title || 'Klasör'}</option>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setCreateDialogOpen(false)} sx={dialogSecondaryButtonSx}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreatePresentation}
            sx={dialogPrimaryButtonSx}
          >
            Oluştur
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createAnalysisOpen}
        onClose={() => setCreateAnalysisOpen(false)}
        {...dialogBaseProps}
      >
        <DialogTitle sx={dialogTitleSx}>Yeni Analiz Oluştur</DialogTitle>
        <DialogContent dividers sx={dialogContentSx}>
          <TextField
            autoFocus
            fullWidth
            label="Analiz Başlığı"
            value={newAnalysisTitle}
            onChange={(e) => setNewAnalysisTitle(e.target.value)}
            sx={{ ...dialogFieldSx, mb: 2 }}
          />
          <TextField
            fullWidth
            select
            label="Veri Seti"
            value={newAnalysisProjectID}
            onChange={(e) => setNewAnalysisProjectID(e.target.value)}
            SelectProps={{ native: true }}
            sx={dialogFieldSx}
          >
            <option value="" disabled>Veri seti seçin</option>
            {projects.map((p) => (
              <option key={p.projectID} value={p.projectID}>
                {p.projectCode}
              </option>
            ))}
          </TextField>
          <TextField
            fullWidth
            select
            label="Klasör"
            value={newAnalysisFolderId}
            onChange={(e) => setNewAnalysisFolderId(e.target.value)}
            SelectProps={{ native: true }}
            sx={{ ...dialogFieldSx, mt: 2 }}
          >
            <option value="">Klasörsüz Öğeler</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>{f.title || 'Klasör'}</option>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setCreateAnalysisOpen(false)} sx={dialogSecondaryButtonSx}>
            {t('cancel')}
          </Button>
          <Button variant="contained" onClick={handleCreateAnalysis} sx={dialogPrimaryButtonSx}>
            Oluştur
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={renameAnalysisOpen}
        onClose={() => setRenameAnalysisOpen(false)}
        {...dialogBaseProps}
      >
        <DialogTitle sx={dialogTitleSx}>Analiz Adını Değiştir</DialogTitle>
        <DialogContent dividers sx={dialogContentSx}>
          <TextField
            autoFocus
            fullWidth
            label="Analiz Başlığı"
            value={renameAnalysisTitle}
            onChange={(e) => setRenameAnalysisTitle(e.target.value)}
            sx={dialogFieldSx}
          />
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setRenameAnalysisOpen(false)} sx={dialogSecondaryButtonSx}>
            {t('cancel')}
          </Button>
          <Button variant="contained" onClick={handleRenameAnalysis} sx={dialogPrimaryButtonSx}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={changeAnalysisOpen}
        onClose={() => setChangeAnalysisOpen(false)}
        {...dialogBaseProps}
      >
        <DialogTitle sx={dialogTitleSx}>Analizin Veri Setini Değiştir</DialogTitle>
        <DialogContent dividers sx={dialogContentSx}>
          <TextField
            fullWidth
            select
            label="Veri Seti"
            value={changeAnalysisProjectID}
            onChange={(e) => setChangeAnalysisProjectID(e.target.value)}
            SelectProps={{ native: true }}
            sx={dialogFieldSx}
          >
            <option value="" disabled>Veri seti seçin</option>
            {projects.map((p) => (
              <option key={p.projectID} value={p.projectID}>
                {p.projectCode}
              </option>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setChangeAnalysisOpen(false)} sx={dialogSecondaryButtonSx}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleChangeAnalysisProject}
            sx={dialogPrimaryButtonSx}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        {...dialogBaseProps}
      >
        <DialogTitle sx={dialogTitleSx}>Sunum Adını Değiştir</DialogTitle>
        <DialogContent dividers sx={dialogContentSx}>
          <TextField
            autoFocus
            fullWidth
            label="Sunum Başlığı"
            value={renameTitle}
            onChange={(e) => setRenameTitle(e.target.value)}
            sx={dialogFieldSx}
          />
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setRenameDialogOpen(false)} sx={dialogSecondaryButtonSx}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleRenamePresentation}
            sx={dialogPrimaryButtonSx}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={renameProjectOpen}
        onClose={() => setRenameProjectOpen(false)}
        {...dialogBaseProps}
      >
        <DialogTitle sx={dialogTitleSx}>Veri Seti Adını Değiştir</DialogTitle>
        <DialogContent dividers sx={dialogContentSx}>
          <TextField
            autoFocus
            fullWidth
            label="Veri Seti Başlığı"
            value={renameProjectTitle}
            onChange={(e) => setRenameProjectTitle(e.target.value)}
            sx={dialogFieldSx}
          />
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button onClick={() => setRenameProjectOpen(false)} sx={dialogSecondaryButtonSx}>
            {t('cancel')}
          </Button>
          <Button variant="contained" onClick={handleRenameProject} sx={dialogPrimaryButtonSx}>
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
